/**
 * Filter Handlers
 * Handle filter change events (checkboxes, selects)
 */

import { state, saveLiveChoice } from '../core/state.js';
import { normalizeBrandName, normalizeBrands } from '../utils/brand-utils.js';

// Will be injected by index.js
let performSearchAuto = null;
let updateFiltersCount = null;
let filterDebounce = null;

const isDesktop = window.innerWidth >= 1280;

/**
 * Set dependencies
 */
export function setDependencies(deps) {
  performSearchAuto = deps.performSearchAuto;
  updateFiltersCount = deps.updateFiltersCount;
  filterDebounce = deps.filterDebounce;
}

/**
 * Handle filter change event
 * Updates state and triggers search
 * @param {Event} event - Change event from checkbox/select
 */
export function handleFilterChange(event) {
  const filterType = event.target.dataset.filter;
  const value = event.target.value;
  const checked = event.target.checked;

  console.log('🔄 Filter changed:', { filterType, value, checked });

  if (filterType === 'sportSlug') {
    if (checked) {
      state.sportSlug.push(value);
    } else {
      state.sportSlug = state.sportSlug.filter((v) => v !== value);
    }
  } else if (filterType === 'types') {
    if (checked) {
      // Only add if not already present (case-insensitive)
      const exists = state.types.some(
        (v) => v.toLowerCase() === value.toLowerCase()
      );
      if (!exists) state.types.push(value);
    } else {
      // Remove case-insensitively (preset may be lowercase, facet may be capitalized)
      state.types = state.types.filter(
        (v) => v.toLowerCase() !== value.toLowerCase()
      );
    }
  } else if (filterType === 'genderFilter') {
    // Handle new gender filter checkboxes (Coed, Male only, Female only)
    if (value === 'coed') {
      state.filterCoed = checked;
    } else if (value === 'maleOnly') {
      state.filterMaleOnly = checked;
    } else if (value === 'femaleOnly') {
      state.filterFemaleOnly = checked;
    }
  } else if (filterType === 'brands') {
    // Brand page mode: two independent checkboxes, Algolia filter derived from both flags
    if (state.brandPageMode && state.brandPageBrand) {
      if (value === '_all_other_brands') {
        state.brandPageAllOthers = checked;
      } else {
        state.brandPageBrandSelected = checked;
      }
      // Derive Algolia filters from visual checkbox state:
      // - brand only checked  → include filter (brands:"Nike")
      // - all others only     → exclude filter (NOT brands:"Nike")
      // - both or neither     → no filter
      if (state.brandPageBrandSelected && !state.brandPageAllOthers) {
        // Use brandPageBrand directly (not normalizeBrands) — current facets may not
        // contain it after a toggle cycle, which would strip the filter and show all camps.
        state.brand = [state.brandPageBrand];
        state.brandExclude = [];
      } else if (!state.brandPageBrandSelected && state.brandPageAllOthers) {
        state.brand = [];
        state.brandExclude = [state.brandPageBrand];
      } else {
        state.brand = [];
        state.brandExclude = [];
      }

      if (updateFiltersCount) updateFiltersCount();
      if (isDesktop && filterDebounce && performSearchAuto) {
        filterDebounce().then(() => performSearchAuto());
      }
      return;
    }

    if (checked) {
      const normalizedValue = normalizeBrandName(value);
      // Only add if brand exists in facets
      if (normalizedValue) {
        // Check if already exists (case-insensitive)
        const exists = state.brand.some(
          (b) => b.toLowerCase() === normalizedValue.toLowerCase()
        );
        if (!exists) {
          state.brand.push(normalizedValue);
        }
      }
    } else {
      // Remove by case-insensitive comparison
      state.brand = state.brand.filter(
        (v) => v.toLowerCase() !== value.toLowerCase()
      );
    }
    // Re-normalize to remove any duplicates and invalid brands
    state.brand = normalizeBrands(state.brand);
  } else if (filterType === 'skills') {
    if (checked) {
      const exists = state.skills.some(
        (v) => v.toLowerCase() === value.toLowerCase()
      );
      if (!exists) state.skills.push(value);
    } else {
      state.skills = state.skills.filter(
        (v) => v.toLowerCase() !== value.toLowerCase()
      );
    }
  } else if (filterType === 'live') {
    // When checked: live = true (show only live camps, add live=1 to URL)
    // When unchecked: live = null (show all camps, remove parameter from URL)
    state.live = checked ? true : null;
    // Remember the choice for later searches in this session
    saveLiveChoice(checked);
  }

  // Track filter change in Algolia Analytics
  if (window.AlgoliaAnalyticsHandlers && checked) {
    const queryID = localStorage.getItem('algoliaQueryID');
    if (queryID) {
      let filterValue = value;
      let filterName = filterType;

      // Map filter values and names for analytics
      if (filterType === 'genderFilter') {
        filterName = 'gender';
        if (value === 'coed') filterValue = 'all';
        else if (value === 'maleOnly') filterValue = 'male';
        else if (value === 'femaleOnly') filterValue = 'female';
      } else if (filterType === 'types') {
        // Types are stored as feeCodes in Algolia
        filterName = 'feeCodes';
      } else if (filterType === 'brands') {
        // Use normalized brand name if available
        const normalizedValue = normalizeBrandName(value);
        if (normalizedValue) {
          filterValue = normalizedValue;
        }
      } else if (filterType === 'live') {
        filterValue = 'true';
      } else if (filterType === 'sportSlug') {
        // Keep as is
      }

      window.AlgoliaAnalyticsHandlers.trackFilter(
        filterName,
        filterValue,
        queryID
      );
    }
  }

  // Update counters immediately (important on mobile where search doesn't auto-run)
  if (updateFiltersCount) updateFiltersCount();
  if (isDesktop && filterDebounce && performSearchAuto) {
    filterDebounce().then(() => {
      performSearchAuto();
    });
  }
}

console.log('📦 Filter handlers module loaded');
